import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './AccountPage.css';

export default function AccountPage() {
  const { logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [linkRequests, setLinkRequests] = useState([]);
  const [resolvingId, setResolvingId] = useState(null);
  const [copiedKey, setCopiedKey] = useState('');

  const handleCopy = async (value, key) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey((prev) => (prev === key ? '' : prev)), 1500);
    } catch (err) {
      console.error('Failed to copy value:', err);
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const [dataRes, requestsRes] = await Promise.all([
          api.get('/users/me/data'),
          api.get('/users/me/link-requests'),
        ]);
        setData(dataRes.data.data);
        setLinkRequests(requestsRes.data.requests || []);
      } catch (err) {
        console.error('Failed to load account data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const handleLinkRequest = async (requestId, action) => {
    setResolvingId(requestId);
    try {
      await api.post(`/users/me/link-requests/${requestId}/${action}`);
      setLinkRequests(prev => prev.filter(r => r._id !== requestId));
      if (action === 'approve') {
        // Refresh data to pick up the newly approved linkedApp
        const dataRes = await api.get('/users/me/data');
        setData(dataRes.data.data);
      }
    } catch (err) {
      alert(err?.response?.data?.message || `Failed to ${action} request`);
    } finally {
      setResolvingId(null);
    }
  };

  const handleDisconnectApp = async (appKey) => {
    if (!window.confirm(`Disconnect from ${appKey}? The other app will need to re-request a link.`)) return;
    try {
      await api.delete(`/users/me/linked-apps/${appKey}`);
      const dataRes = await api.get('/users/me/data');
      setData(dataRes.data.data);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to disconnect');
    }
  };

  const handlePermanentlyDeleteFile = async (fileId) => {
    if (!window.confirm('Permanently delete this file? This cannot be undone.')) return;

    setDeletingFileId(fileId);
    try {
      await api.delete(`/files/${fileId}`);
      setData((prev) => ({
        ...prev,
        orphanedItems: {
          ...prev.orphanedItems,
          files: prev.orphanedItems.files.filter((f) => f.id !== fileId),
        },
      }));
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to delete file');
    } finally {
      setDeletingFileId(null);
    }
  };

  const handleExportData = () => {
    if (!data) return;
    const exportData = {
      exportedAt: new Date().toISOString(),
      profile: data.profile,
      statistics: data.statistics,
      files: data.files,
      orphanedItems: data.orphanedItems,
    };
    const jsonStr = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `comsapp-data-export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleOpenInNewTab = () => {
    window.open('/account', '_blank');
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (iso) => {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="account-page">
        <div className="account-container">
          <p>Loading account data...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="account-page">
        <div className="account-container">
          <p>Failed to load account data.</p>
        </div>
      </div>
    );
  }

  const { profile, statistics, files, orphanedItems } = data;

  return (
    <div className="account-page">
      <div className="account-container">
        {/* Header */}
        <div className="account-header">
          <div className="account-profile">
            <div className="account-avatar">
              {profile.avatar || (profile.name?.[0]?.toUpperCase() || '?')}
            </div>
            <div className="account-info">
              <h1>{profile.name}</h1>
              <p className="account-email">{profile.email}</p>
              <p className="account-role">{profile.role?.toUpperCase() || 'USER'}</p>
              <div className="account-uid-row" title="Your unique user ID">
                <p className="account-uid">ID: {profile.id}</p>
                <button
                  type="button"
                  className="copy-id-btn"
                  onClick={() => handleCopy(profile.id, 'profile-id')}
                  title="Copy user ID"
                  aria-label="Copy user ID"
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                    <path d="M9 9h10v12H9z" fill="none" stroke="currentColor" strokeWidth="1.8"/>
                    <path d="M5 3h10v4" fill="none" stroke="currentColor" strokeWidth="1.8"/>
                    <path d="M5 3v12h4" fill="none" stroke="currentColor" strokeWidth="1.8"/>
                  </svg>
                  <span>{copiedKey === 'profile-id' ? 'Copied' : 'Copy'}</span>
                </button>
              </div>
            </div>
          </div>
          <div className="account-header-actions">
            <button className="btn btn-sm btn-outline" onClick={handleExportData} title="Export all data as JSON">
              ⬇ Export
            </button>
            <button className="btn btn-sm btn-outline" onClick={handleOpenInNewTab} title="Open in new tab">
              ↗ New Tab
            </button>
            <button className="btn btn-outline" onClick={logout}>
              Logout
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="account-tabs">
          <button
            className={`account-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`account-tab ${activeTab === 'files' ? 'active' : ''}`}
            onClick={() => setActiveTab('files')}
          >
            Files ({statistics.files})
          </button>
          <button
            className={`account-tab ${activeTab === 'orphaned' ? 'active' : ''}`}
            onClick={() => setActiveTab('orphaned')}
          >
            Orphaned Items ({orphanedItems.files.length})
          </button>
          <button
            className={`account-tab ${activeTab === 'connections' ? 'active' : ''}`}
            onClick={() => setActiveTab('connections')}
          >
            Connected Apps{linkRequests.length > 0 && (
              <span className="account-tab-badge">{linkRequests.length}</span>
            )}
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="account-tab-content">
            <div className="account-stats-grid">
              <div className="account-stat-card">
                <div className="stat-label">Messages Sent</div>
                <div className="stat-value">{statistics.sentMessages}</div>
              </div>
              <div className="account-stat-card">
                <div className="stat-label">Files</div>
                <div className="stat-value">{statistics.files}</div>
              </div>
              <div className="account-stat-card">
                <div className="stat-label">Deleted Messages</div>
                <div className="stat-value">{statistics.deletedMessages}</div>
              </div>
              <div className="account-stat-card">
                <div className="stat-label">Orphaned Files</div>
                <div className="stat-value">{statistics.orphanedFiles}</div>
              </div>
            </div>

            <div className="account-info-section">
              <h3>Account Created</h3>
              <p>{formatDate(profile.createdAt)}</p>
            </div>
          </div>
        )}

        {/* Files Tab */}
        {activeTab === 'files' && (
          <div className="account-tab-content">
            {files.length === 0 ? (
              <p className="account-empty">No files yet.</p>
            ) : (
              <div className="account-files-list">
                {files.map((file) => (
                  <div key={file.id} className="account-file-row">
                    <div className="file-icon">📄</div>
                    <div className="file-details">
                      <div className="file-name">{file.name}</div>
                      <div className="file-meta">
                        {formatBytes(file.size)} • {formatDate(file.createdAt)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Orphaned Items Tab */}
        {activeTab === 'orphaned' && (
          <div className="account-tab-content">
            {orphanedItems.files.length === 0 ? (
              <p className="account-empty">No orphaned files.</p>
            ) : (
              <>
                <p className="account-section-notice">
                  These files are marked for cleanup and will be automatically deleted after 30 days.
                </p>
                <div className="account-orphaned-list">
                  {orphanedItems.files.map((file) => (
                    <div
                      key={file.id}
                      className={`account-orphaned-row ${file.expiredForCleanup ? 'expired' : ''}`}
                    >
                      <div className="orphaned-icon">🗑</div>
                      <div className="orphaned-details">
                        <div className="orphaned-name">{file.name}</div>
                        <div className="orphaned-meta">
                          {formatBytes(file.size)} • Deleted {formatDate(file.deletedAt)}
                        </div>
                        {file.expiredForCleanup && (
                          <div className="orphaned-status">Eligible for permanent cleanup</div>
                        )}
                      </div>
                      {file.expiredForCleanup && (
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handlePermanentlyDeleteFile(file.id)}
                          disabled={deletingFileId === file.id}
                        >
                          {deletingFileId === file.id ? 'Deleting...' : 'Delete'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            {orphanedItems.messages.length > 0 && (
              <div className="account-info-section">
                <h3>Deleted Messages</h3>
                <p>{orphanedItems.messages.length} message(s) soft-deleted</p>
              </div>
            )}
          </div>
        )}

        {/* Connected Apps Tab */}
        {activeTab === 'connections' && (
          <div className="account-tab-content">
            {/* Currently connected apps */}
            {data?.linkedApps && Object.keys(data.linkedApps).length > 0 && (
              <div className="linked-apps-section">
                <h3 className="account-section-title">Connected</h3>
                <div className="linked-apps-list">
                  {Object.entries(data.linkedApps).map(([appKey, appVal]) => (
                    <div key={appKey} className="linked-app-card">
                      <div className="linked-app-icon">✅</div>
                      <div className="linked-app-info">
                        <div className="linked-app-name">
                          {appKey === 'rs-education' ? 'RS Education' : appKey}
                        </div>
                        <div className="linked-app-detail">ID: <code>{appVal.userId}</code></div>
                        {appVal.linkedAt && (
                          <div className="linked-app-detail muted">
                            Connected {new Date(appVal.linkedAt).toLocaleDateString(undefined, {
                              year: 'numeric', month: 'short', day: 'numeric',
                            })}
                          </div>
                        )}
                      </div>
                      <button
                        className="btn btn-sm btn-outline btn-danger-outline"
                        onClick={() => handleDisconnectApp(appKey)}
                        title="Disconnect this app"
                      >
                        Disconnect
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pending link requests */}
            <h3 className="account-section-title" style={{ marginTop: data?.linkedApps && Object.keys(data.linkedApps).length > 0 ? '1.5rem' : 0 }}>
              Pending Requests {linkRequests.length > 0 && <span className="account-tab-badge">{linkRequests.length}</span>}
            </h3>
            {linkRequests.length === 0 ? (
              <p className="account-empty" style={{ textAlign: 'left', padding: '0.5rem 0' }}>No pending connection requests.</p>
            ) : (
              <div className="link-requests-list">
                {linkRequests.map((req) => (
                  <div key={req._id} className="link-request-card">
                    <div className="link-request-icon">🔗</div>
                    <div className="link-request-info">
                      <div className="link-request-app">{req.fromApp === 'rs-education' ? 'RS Education' : req.fromApp}</div>
                      {req.fromUserName && (
                        <div className="link-request-user">{req.fromUserName}</div>
                      )}
                      <div className="link-request-id">ID: <code>{req.fromUserId}</code></div>
                      <div className="link-request-date">
                        Requested {new Date(req.requestedAt).toLocaleDateString(undefined, {
                          year: 'numeric', month: 'short', day: 'numeric',
                        })}
                      </div>
                    </div>
                    <div className="link-request-actions">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleLinkRequest(req._id, 'approve')}
                        disabled={resolvingId === req._id}
                      >
                        {resolvingId === req._id ? '…' : 'Approve'}
                      </button>
                      <button
                        className="btn btn-sm btn-outline btn-danger-outline"
                        onClick={() => handleLinkRequest(req._id, 'reject')}
                        disabled={resolvingId === req._id}
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {(!data?.linkedApps || Object.keys(data.linkedApps).length === 0) && linkRequests.length === 0 && (
              <p className="account-empty">No connected apps yet.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
